import Vue from "vue";
import VueRouter, { NavigationGuardNext, Route } from "vue-router";
import VueGtm from "vue-gtm";
import VueGtag from "vue-gtag";
import ErrorNotFound from "@/components/Pages/Error/NotFound";
import { setGaPageTitle, setTitle, setNoindex } from "@/helpers/meta-helper";
import {
  authorizationSignInToken,
  getPartnerUserAndContracts,
  hasToken,
  setGAParams,
  setGAUserParams,
  setTokenToStore
} from "@/helpers/auth-helper";
import {
  hasPrivilege,
  PRIVILEGE_AGENT_SCOUT,
  PRIVILEGE_COMPANY_PROPS_WRITE,
  PRIVILEGE_ENTRIES_READ,
  PRIVILEGE_ENTRY_MAIL_TEMPLATE_WRITE,
  PRIVILEGE_FILES_READ,
  PRIVILEGE_OFFERS_READ,
  PRIVILEGE_OFFERS_WRITE,
  PRIVILEGE_SCOUT
} from "@/helpers/privilege-helper";
import {
  EXTERNAL_DATA_PROCESSORS,
  TERMS_OF_OUTSOURCING_NAME,
  TERMS_OF_SUBCONTRACTING_NAME,
  TERMS_OF_USE_FOR_JOB_NAME,
  TERMS_OF_USE_FOR_PLAN_NAME,
  TERMS_OF_USE_FOR_SCOUT_NAME,
  TERMS_OF_USE_FOR_AGENT_SCOUT_NAME,
  TERMS_OF_USE_FOR_AGENT_NAME,
  TERMS_OF_USE_FOR_ADVERTORIAL_NAME,
  TERMS_OF_USE_FOR_ONLINE_EVENT_NAME,
  TERMS_OF_USE_FOR_MOVIE_NAME,
  TERMS_OF_USE_FOR_EVENT_APPLICATION_NAME,
  TERMS_OF_USE_FOR_LEASE_NAME,
  TERMS_OF_USE_FOR_MOVIE_PRODUCTION
} from "@/constants/terms";
import { resetZendeskWidget, setZendeskWidget } from "@/helpers/zendesk-helper";
import {
  redirectToInitialPage,
  redirectWhenIsNotOnboardingFinished
} from "@/helpers/router-helper";
import { setSentryUser } from "@/helpers/sentry-helper";

import store from "@/store";
import { HttpStatusCode } from "@/api/types";
import { sendGaEvent } from "@/helpers/ga-helper";
import {
  checkAtsApproved,
  checkCalendarReviewApproved,
  checkCheckinApproved,
  checkVideoBundleUnlockingLogsApproved,
  hasScoutAgentContract
} from "@/helpers/contract-helper";
import { useComponentInstance } from "@/helpers/hooks/useComponentInstance";
import { MenuType } from "@/store/domains/user/types";
import {
  restoreMenuTypeFromLocalStorage,
  saveMenuTypeToLocalStorage
} from "@/helpers/menu-type-helper";

if (!process || process.env.NODE_ENV !== "test") {
  Vue.use(VueRouter);
}

export type RouteName = typeof routes[number]["name"];

const ensureCalendarReviewApproved = function(next: NavigationGuardNext): void {
  const contracts = store.state.domains.user.partnerUser?.partner.contracts;
  const isCalendarReviewApproved = contracts?.find(contract =>
    checkCalendarReviewApproved(contract)
  );

  if (isCalendarReviewApproved) {
    next();
  } else {
    store.commit("common/updateApiError", {
      code: HttpStatusCode.Forbidden,
      message: "Forbidden"
    });
  }
};

const ensureCheckinApproved = function(next: NavigationGuardNext): void {
  const contracts = store.state.domains.user.partnerUser?.partner.contracts;
  const isCheckinApproved = contracts?.find(contract =>
    checkCheckinApproved(contract)
  );

  if (isCheckinApproved) {
    next();
  } else {
    store.commit("common/updateApiError", {
      code: HttpStatusCode.Forbidden,
      message: "Forbidden"
    });
  }
};

const ensureAtsApproved = function(next: NavigationGuardNext): void {
  const contracts = store.state.domains.user.partnerUser?.partner.contracts;
  const isAtsApproved = contracts?.find(contract => checkAtsApproved(contract));

  if (isAtsApproved) {
    next();
  } else {
    store.commit("common/updateApiError", {
      code: HttpStatusCode.Forbidden,
      message: "Forbidden"
    });
  }
};

const ensureVideoBundleUnlockingLogsApproved = function(
  next: NavigationGuardNext
): void {
  const contracts = store.state.domains.user.partnerUser?.partner.contracts;
  const isVideoBundleUnlockingLogsApproved = contracts?.find(contract =>
    checkVideoBundleUnlockingLogsApproved(contract)
  );

  if (isVideoBundleUnlockingLogsApproved) {
    next();
  } else {
    store.commit("common/updateApiError", {
      code: HttpStatusCode.Forbidden,
      message: "Forbidden"
    });
  }
};

const ensureScoutAgentContract = function(next: NavigationGuardNext): void {
  const availableContracts = store.state.domains.user.availableContracts ?? [];
  const expiredContracts = store.state.domains.user.expiredContracts ?? [];

  // エージェントプランの契約が期限切れの場合でも画面の閲覧はさせたいため、期限切れの契約も含めて判定する
  if (hasScoutAgentContract(availableContracts.concat(expiredContracts))) {
    // スカウトエージェントメニューが選択されていなかった場合、スカウトエージェントメニューを選択させる
    if (!store.getters["domains/user/isScoutAgentMenuActive"]) {
      store.commit("domains/user/changeMenuType", MenuType.MENU_SCOUT_AGENT);
    }
    next();
  } else {
    store.commit("common/updateApiError", {
      code: HttpStatusCode.Forbidden,
      message: "Forbidden"
    });
  }
};

const ensureScoutPrivilege = function(next: NavigationGuardNext): void {
  // エージェント画面表示中にエージェント向けスカウト機能の権限がない場合は人事画面に戻すようにしているため、人事画面表示中のみ権限チェックを行う
  // c.f. https://github.com/OneCareerJP/cloud/blob/77f94db2ca9007a0764de6edf348d909dfd92288/frontend/src/router/index.ts#L934-L939
  if (
    !store.getters["domains/user/isScoutAgentMenuActive"] &&
    !hasPrivilege(PRIVILEGE_SCOUT)
  ) {
    store.commit("common/updateApiError", {
      code: HttpStatusCode.Forbidden,
      message: "Forbidden"
    });
    return;
  }

  next();
};

const routes = [
  {
    path: "/",
    name: "Home" as const,
    meta: {},
    component: () => import("@/components/Pages/Dashboard/index.vue")
  },
  {
    path: "/job/events",
    name: "JobEvents" as const,
    meta: {
      title: "イベント一覧"
    },
    component: () => import("@/components/Pages/JobEvents/index.vue")
  },
  {
    path: "/job/new-event",
    name: "NewJobEvent" as const,
    meta: {
      title: "イベント作成",
      privilege: true,
      predicate: PRIVILEGE_OFFERS_WRITE
    },
    component: () => import("@/components/Pages/JobEvent/index.vue")
  },
  {
    path: "/job/events/:eventId",
    name: "JobEvent" as const,
    meta: {
      title: "イベント詳細",
      privilege: true,
      predicate: PRIVILEGE_OFFERS_READ
    },
    component: () => import("@/components/Pages/JobEvent/index.vue")
  },
  {
    path: "/job/events/:eventId/preview",
    name: "JobEventPreview" as const,
    meta: {
      title: "イベントプレビュー",
      privilege: true,
      predicate: PRIVILEGE_OFFERS_READ
    },
    component: () => import("@/components/Pages/JobEventPreview/index.vue")
  },
  {
    path: "/job/events/:eventId/schedules",
    name: "JobEventSchedules" as const,
    meta: {
      title: "スケジュール一覧",
      privilege: true,
      predicate: PRIVILEGE_ENTRY_MAIL_TEMPLATE_WRITE
    },
    component: () => import("@/components/Pages/JobEventSchedules/index.vue")
  },
  {
    path: "/job/events/:eventId/schedules/:scheduleId/entry_mail_template",
    name: "JobEventEntryMailTemplate" as const,
    meta: {
      title: "自動返信メールの編集",
      privilege: true,
      predicate: PRIVILEGE_ENTRY_MAIL_TEMPLATE_WRITE
    },
    component: () =>
      import("@/components/Pages/JobEventEntryMailTemplate/index.vue")
  },
  {
    path: "/job/events/:eventId/entries",
    name: "JobEventEntryUsers" as const,
    meta: {
      title: "イベント申込者一覧",
      privilege: true,
      predicate: PRIVILEGE_ENTRIES_READ
    },
    component: () => import("@/components/Pages/JobEventEntryUsers/index.vue")
  },
  {
    path: "/job/events/:eventId/entries/bulk_confirm",
    name: "JobEventEntryUsersEventStatus" as const,
    meta: {
      title: "イベント申込者一括ステータス変更"
    },
    component: () =>
      import("@/components/Pages/JobEventEntryUsers/BulkConfirm/index.vue")
  },
  {
    path: "/job/sponsored_events",
    name: "SponsoredEvents" as const,
    meta: {
      title: "ワンキャリアイベント一覧"
    },
    beforeEnter(_to: Route, _from: Route, next: NavigationGuardNext) {
      ensureCheckinApproved(next);
    },
    component: () => import("@/components/Pages/SponsoredEvents/index.vue")
  },
  {
    path: "/job/sponsored_event_entry_applicants",
    name: "SponsoredEventEntryApplicants" as const,
    meta: {
      title: "オンライン_企業説明会申込者一覧",
      privilege: true,
      predicate: PRIVILEGE_ENTRIES_READ
    },
    component: () =>
      import("@/components/Pages/SponsoredEventEntryApplicants/index.vue")
  },
  {
    path: "/job/sponsored_event_entry_checkins",
    name: "SponsoredEventEntryCheckins" as const,
    meta: {
      title: "オンライン_合説チェックイン一覧"
    },
    beforeEnter(_to: Route, _from: Route, next: NavigationGuardNext) {
      ensureCheckinApproved(next);
    },
    component: () =>
      import("@/components/Pages/SponsoredEventEntryCheckins/index.vue")
  },
  {
    path: "/plans",
    name: "Plans" as const,
    meta: {
      title: "採用計画一覧"
    },
    beforeEnter(_to: Route, _from: Route, next: NavigationGuardNext) {
      ensureCalendarReviewApproved(next);
    },
    component: () => import("@/components/Pages/Plans")
  },
  {
    path: "/plans/:planId",
    name: "Plan" as const,
    meta: {
      title: "採用計画詳細"
    },
    beforeEnter(_to: Route, _from: Route, next: NavigationGuardNext) {
      ensureCalendarReviewApproved(next);
    },
    component: () => import("@/components/Pages/Plan")
  },
  {
    path: "/plans/:planId/report",
    name: "Report" as const,
    meta: {
      title: "レポート"
    },
    beforeEnter(_to: Route, _from: Route, next: NavigationGuardNext) {
      ensureCalendarReviewApproved(next);
    },
    component: () => import("@/components/Pages/Report")
  },
  {
    path: "/event_experiences/:eventExperienceId",
    name: "EventExperience" as const,
    meta: {
      title: "他社イベント体験談詳細"
    },
    beforeEnter(_to: Route, _from: Route, next: NavigationGuardNext) {
      ensureCalendarReviewApproved(next);
    },
    component: () => import("@/components/Pages/EventExperience")
  },
  {
    path: "/event-experiences/:eventExperienceId",
    redirect: { name: "EventExperience" }
  },
  {
    path: "/event_experiences",
    name: "EventExperiences" as const,
    meta: {
      title: "他社イベント体験談一覧"
    },
    beforeEnter(_to: Route, _from: Route, next: NavigationGuardNext) {
      ensureCalendarReviewApproved(next);
    },
    component: () => import("@/components/Pages/EventExperiences")
  },
  {
    path: "/event-experiences",
    redirect: { name: "EventExperiences" }
  },
  {
    path: "/notifications",
    name: "Notifications" as const,
    meta: {
      title: "通知一覧"
    },
    component: () => import("@/components/Pages/Notifications")
  },
  {
    path: "/event_details/:eventDetailId",
    name: "EventDetail" as const,
    meta: {
      title: "他社イベント詳細"
    },
    beforeEnter(_to: Route, _from: Route, next: NavigationGuardNext) {
      ensureCalendarReviewApproved(next);
    },
    component: () => import("@/components/Pages/EventDetail")
  },
  {
    path: "/event-details/:eventDetailId",
    redirect: { name: "EventDetail" }
  },
  {
    path: "/clips",
    name: "Clips" as const,
    meta: {
      title: "クリップ一覧"
    },
    component: () => import("@/components/Pages/Clips")
  },
  {
    path: "/sign_in",
    name: "SignIn" as const,
    meta: {
      title: "ログイン",
      ignoreAuth: true,
      authPage: true
    },
    component: () => import("@/components/Pages/SignIn")
  },
  {
    path: "/signin",
    redirect: { name: "SignIn" }
  },
  {
    // NOTE: "/authorization/sign_in/token" にするためにはバックエンドの対応が必要
    path: "/authorization/signin/token",
    name: "AuthorizationSignInToken" as const
  },
  {
    path: "/settings/notification",
    name: "SettingsNotification" as const,
    meta: {
      title: "メール受信設定"
    },
    component: () => import("@/components/Pages/Settings/Notification")
  },
  {
    path: "/settings/follows/companies",
    name: "SettingsFollowsCompanies" as const,
    meta: {
      title: "フォロー中の企業"
    },
    beforeEnter(_to: Route, _from: Route, next: NavigationGuardNext) {
      ensureCalendarReviewApproved(next);
    },
    component: () =>
      import("@/components/Pages/Settings/Follows/Companies/index.vue")
  },
  {
    path: "/settings/follows/universities",
    name: "SettingsFollowsUniversities" as const,
    meta: {
      title: "フォロー中の大学"
    },
    beforeEnter(_to: Route, _from: Route, next: NavigationGuardNext) {
      ensureCalendarReviewApproved(next);
    },
    component: () =>
      import("@/components/Pages/Settings/Follows/Universities/index.vue")
  },
  {
    path: "/password/reissue",
    name: "PasswordReissue" as const,
    meta: {
      title: "パスワード再発行",
      ignoreAuth: true,
      authPage: true
    },
    component: () => import("@/components/Pages/Password/Reissue")
  },
  {
    path: "/password/wait",
    name: "PasswordWait" as const,
    meta: {
      title: "メール送信完了",
      ignoreAuth: true,
      authPage: true
    },
    component: () => import("@/components/Pages/Password/Wait")
  },
  {
    path: "/password/error",
    name: "PasswordError" as const,
    meta: {
      title: "有効期限が切れています",
      ignoreAuth: true,
      authPage: true
    },
    component: () => import("@/components/Pages/Password/Error")
  },
  {
    path: "/password/reset",
    name: "PasswordReset" as const,
    meta: {
      title: "パスワード再設定",
      ignoreAuth: true,
      authPage: true
    },
    component: () => import("@/components/Pages/Password/Reset")
  },
  {
    path: "/password",
    redirect: { name: "PasswordReissue" }
  },
  // メール配信設定解除
  {
    path: "/edit_notification",
    name: "EditNotification" as const,
    meta: {
      title: "メール配信設定解除",
      ignoreAuth: true
    },
    component: () => import("@/components/Pages/EditNotification/index.vue")
  },
  // 旧利用規約ページURLにアクセスした際のリダイレクト
  {
    path: "/terms_of_use",
    redirect: { name: TERMS_OF_USE_FOR_PLAN_NAME }
  },
  {
    path: "/terms-of-use(.html)?",
    redirect: { name: TERMS_OF_USE_FOR_PLAN_NAME }
  },
  {
    path: "/terms_of_use_for_job",
    name: TERMS_OF_USE_FOR_JOB_NAME,
    meta: {
      title: "利用規約（求人掲載）",
      ignoreAuth: true
    },
    component: () => import("@/components/Pages/TermsOfUseForJob/index.vue")
  },
  {
    path: "/terms-of-use-for-job(.html)?",
    redirect: { name: TERMS_OF_USE_FOR_JOB_NAME }
  },
  {
    path: "/terms_of_use_for_plan",
    name: TERMS_OF_USE_FOR_PLAN_NAME,
    meta: {
      title: "利用規約（採用計画）",
      ignoreAuth: true
    },
    component: () => import("@/components/Pages/TermsOfUseForPlan/index.vue")
  },
  {
    path: "/terms-of-use-for-plan(.html)?",
    redirect: { name: TERMS_OF_USE_FOR_PLAN_NAME }
  },
  {
    path: "/terms_of_use_for_scout",
    name: TERMS_OF_USE_FOR_SCOUT_NAME,
    meta: {
      title: "利用規約（スカウト）",
      ignoreAuth: true
    },
    component: () => import("@/components/Pages/TermsOfUseForScout/index.vue")
  },
  {
    path: "/terms-of-use-for-scout(.html)?",
    redirect: { name: TERMS_OF_USE_FOR_SCOUT_NAME }
  },
  {
    path: "/terms_of_use_for_agent_scout",
    name: TERMS_OF_USE_FOR_AGENT_SCOUT_NAME,
    meta: {
      title: "利用規約（エージェントスカウト）",
      ignoreAuth: true
    },
    component: () =>
      import("@/components/Pages/TermsOfUseForAgentScout/index.vue")
  },
  {
    path: "/terms-of-use-for-agent-scout(.html)?",
    redirect: { name: TERMS_OF_USE_FOR_AGENT_SCOUT_NAME }
  },
  {
    path: "/terms_of_outsourcing",
    name: TERMS_OF_OUTSOURCING_NAME,
    meta: {
      title: "利用規約（採用代行）",
      ignoreAuth: true
    },
    component: () => import("@/components/Pages/TermsOfOutsourcing/index.vue")
  },
  {
    path: "/terms-of-outsourcing(.html)?",
    redirect: { name: TERMS_OF_OUTSOURCING_NAME }
  },
  {
    path: "/terms_of_subcontracting",
    name: TERMS_OF_SUBCONTRACTING_NAME,
    meta: {
      title: "利用規約（業務委託）",
      ignoreAuth: true
    },
    component: () =>
      import("@/components/Pages/TermsOfSubcontracting/index.vue")
  },
  {
    path: "/terms-of-subcontracting(.html)?",
    redirect: { name: TERMS_OF_SUBCONTRACTING_NAME }
  },
  {
    path: "/terms_of_use_for_agent",
    name: TERMS_OF_USE_FOR_AGENT_NAME,
    meta: {
      title: "利用規約（新卒紹介）",
      ignoreAuth: true
    },
    component: () => import("@/components/Pages/TermsOfUseForAgent/index.vue")
  },
  {
    path: "/terms-of-use-for-agent(.html)?",
    redirect: { name: TERMS_OF_USE_FOR_AGENT_NAME }
  },
  {
    path: "/terms_of_use_for_lease",
    name: TERMS_OF_USE_FOR_LEASE_NAME,
    meta: {
      title: "利用規約（賃貸借）",
      ignoreAuth: true
    },
    component: () => import("@/components/Pages/TermsOfUseForLease/index.vue")
  },
  {
    path: "/terms-of-use-for-lease(.html)?",
    redirect: { name: TERMS_OF_USE_FOR_LEASE_NAME }
  },
  {
    path: "/terms_of_use_for_advertorial",
    name: TERMS_OF_USE_FOR_ADVERTORIAL_NAME,
    meta: {
      title: "利用規約（メディア広告サービス）",
      ignoreAuth: true
    },
    component: () =>
      import("@/components/Pages/TermsOfUseForAdvertorial/index.vue")
  },
  {
    path: "/terms-of-use-for-advertorial(.html)?",
    redirect: { name: TERMS_OF_USE_FOR_ADVERTORIAL_NAME }
  },
  {
    path: "/terms_of_use_for_online_event",
    name: TERMS_OF_USE_FOR_ONLINE_EVENT_NAME,
    meta: {
      title: "利用規約（オンラインイベント）",
      ignoreAuth: true
    },
    component: () =>
      import("@/components/Pages/TermsOfUseForOnlineEvent/index.vue")
  },
  {
    path: "/terms-of-use-for-online-event(.html)?",
    redirect: { name: TERMS_OF_USE_FOR_ONLINE_EVENT_NAME }
  },
  {
    path: "/terms_of_use_for_movie",
    name: TERMS_OF_USE_FOR_MOVIE_NAME,
    meta: {
      title: "利用規約（OCCカンタン採用動画）",
      ignoreAuth: true
    },
    component: () => import("@/components/Pages/TermsOfUseForMovie/index.vue")
  },
  {
    path: "/terms-of-use-for-movie(.html)?",
    redirect: { name: TERMS_OF_USE_FOR_MOVIE_NAME }
  },
  {
    path: "/terms_of_use_for_event_application",
    name: TERMS_OF_USE_FOR_EVENT_APPLICATION_NAME,
    meta: {
      title: "利用規約（イベント出展申し込み）",
      ignoreAuth: true
    },
    component: () =>
      import("@/components/Pages/TermsOfUseForEventApplication/index.vue")
  },
  {
    path: "/terms_of_use_for_movie_production",
    name: TERMS_OF_USE_FOR_MOVIE_PRODUCTION,
    meta: {
      title: "利用規約（動画制作）",
      ignoreAuth: true
    },
    component: () =>
      import("@/components/Pages/TermsOfUseForMovieProduction/index.vue")
  },
  {
    path: "/terms-of-use-for-event-application(.html)?",
    redirect: { name: TERMS_OF_USE_FOR_EVENT_APPLICATION_NAME }
  },
  {
    path: "/external_data_processors",
    name: EXTERNAL_DATA_PROCESSORS,
    meta: {
      title: "利用者情報の外部送信について",
      ignoreAuth: true
    },
    component: () =>
      import("@/components/Pages/ExternalDataProcessors/index.vue")
  },
  {
    path: "/terms_of_privacy_for_partners",
    name: "TermsOfPrivacyForPartners" as const,
    meta: {
      title: "個人情報の取扱い",
      ignoreAuth: true
    },
    component: () =>
      import("@/components/Pages/TermsOfPrivacyForPartners/index.vue")
  },
  {
    path: "/terms-of-privacy-for-partners(.html)?",
    redirect: { name: "TermsOfPrivacyForPartners" }
  },
  {
    path: "/companies",
    name: "Companies" as const,
    meta: {
      title: "企業検索"
    },
    beforeEnter(_to: Route, _from: Route, next: NavigationGuardNext) {
      ensureCalendarReviewApproved(next);
    },
    component: () => import("@/components/Pages/Companies")
  },
  {
    path: "/companies/:companyId",
    name: "CompanyDetail" as const,
    meta: {
      title: "企業詳細"
    },
    beforeEnter(_to: Route, _from: Route, next: NavigationGuardNext) {
      ensureCalendarReviewApproved(next);
    },
    component: () => import("@/components/Pages/CompanyDetail")
  },
  {
    path: "/companies/:companyId/compare",
    name: "CompanyCompare" as const,
    meta: {
      title: "企業比較"
    },
    beforeEnter(_to: Route, _from: Route, next: NavigationGuardNext) {
      ensureCalendarReviewApproved(next);
    },
    component: () => import("@/components/Pages/CompanyCompare/index.vue")
  },
  {
    path: "/rankings/favorites",
    name: "RankingsFavorites" as const,
    meta: {
      title: "お気に入りランキング"
    },
    beforeEnter(_to: Route, _from: Route, next: NavigationGuardNext) {
      ensureCalendarReviewApproved(next);
    },
    component: () => import("@/components/Pages/Rankings/Favorites")
  },
  {
    path: "/reviews/offers",
    name: "ReviewOffers" as const,
    meta: {
      title: "クチコミを集める",
      sideNav: true
    },
    beforeEnter(_to: Route, _from: Route, next: NavigationGuardNext) {
      ensureCalendarReviewApproved(next);
    },
    component: () => import("@/components/Pages/ReviewOffers")
  },
  {
    path: "/files",
    name: "Files" as const,
    meta: {
      title: "ファイル",
      privilege: true,
      predicate: PRIVILEGE_FILES_READ
    },
    component: () => import("@/components/Pages/Files/index.vue")
  },
  {
    path: "/accounts/partner_users",
    name: "AccountsPartnerUsers" as const,
    meta: {
      title: "アカウント設定 - ユーザー管理"
    },
    component: () =>
      import("@/components/Pages/Accounts/PartnerUsers/index.vue")
  },
  {
    path: "/accounts/partner_users/new",
    name: "AccountsPartnerUsersNew" as const,
    meta: {
      title: "アカウント設定 - ユーザー新規登録"
    },
    component: () =>
      import("@/components/Pages/Accounts/PartnerUser/Create/index.vue")
  },
  {
    path: "/accounts/partner_users/:partnerUserId/edit",
    name: "AccountsPartnerUsersEdit" as const,
    meta: {
      title: "アカウント設定 - ユーザー編集"
    },
    component: () =>
      import("@/components/Pages/Accounts/PartnerUser/Edit/index.vue")
  },
  {
    path: "/accounts/edit_notification",
    name: "AccountsEditNotification" as const,
    meta: {
      title: "アカウント設定 - 通知設定"
    },
    component: () =>
      import("@/components/Pages/Accounts/Notifications/index.vue")
  },
  {
    path: "/accounts/follow_campanies",
    name: "AccountsFollowCampanies" as const,
    meta: {
      title: "アカウント設定 - フォロー設定"
    },
    component: () =>
      import("@/components/Pages/Accounts/Follows/Companies/index.vue")
  },
  {
    path: "/accounts/follow_universities",
    name: "AccountsFollowUniversities" as const,
    meta: {
      title: "アカウント設定 - フォロー設定"
    },
    component: () =>
      import("@/components/Pages/Accounts/Follows/Universities/index.vue")
  },
  {
    path: "/accounts/contracts",
    name: "AccountsContractInformations" as const,
    meta: {
      title: "アカウント設定 - 契約一覧"
    },
    component: () =>
      import("@/components/Pages/Accounts/ContractInformations/index.vue")
  },
  {
    path: "/accounts/integrations/sonar",
    name: "AccountsIntegrationsSonar" as const,
    meta: {
      title: "アカウント設定 - SONAR連携"
    },
    beforeEnter: (_to: Route, _from: Route, next: NavigationGuardNext) => {
      ensureAtsApproved(next);
    },
    component: () =>
      import("@/components/Pages/Accounts/Integrations/Sonar/index.vue")
  },
  {
    path: "/accounts/integrations/i-web",
    name: "AccountsIntegrationsIweb" as const,
    meta: {
      title: "アカウント設定 - i-web連携"
    },
    beforeEnter: (_to: Route, _from: Route, next: NavigationGuardNext) => {
      ensureAtsApproved(next);
    },
    component: () =>
      import("@/components/Pages/Accounts/Integrations/Iweb/index.vue")
  },
  {
    path: "/accounts/privileges",
    name: "AccountsPrivileges" as const,
    meta: {
      title: "アカウント設定 - 権限管理"
    },
    component: () => import("@/components/Pages/Accounts/Privileges/index.vue")
  },
  {
    path: "/partner_entry_users",
    name: "Entries" as const,
    meta: {
      title: "応募者一覧",
      privilege: true,
      predicate: PRIVILEGE_ENTRIES_READ
    },
    component: () => import("@/components/Pages/Entries/index.vue")
  },
  {
    path: "/partner_offline_roundtable_users",
    name: "OfflineRoundtable" as const,
    meta: {
      title: "オフライン_座談会出席者一覧",
      privilege: true,
      predicate: PRIVILEGE_ENTRIES_READ
    },
    component: () => import("@/components/Pages/OfflineRoundtable/index.vue")
  },
  {
    path: "/partner_online_roundtable_users",
    name: "OnlineRoundtable" as const,
    meta: {
      title: "オンライン_座談会出席者一覧",
      privilege: true,
      predicate: PRIVILEGE_ENTRIES_READ
    },
    component: () => import("@/components/Pages/OnlineRoundtable/index.vue")
  },
  {
    path: "/offline_checkin_users",
    name: "OfflineCheckin" as const,
    meta: {
      title: "オフライン_合説ブースチェックイン一覧",
      privilege: true,
      predicate: PRIVILEGE_ENTRIES_READ
    },
    beforeEnter(_to: Route, _from: Route, next: NavigationGuardNext) {
      ensureCheckinApproved(next);
    },
    component: () => import("@/components/Pages/OfflineCheckin/index.vue")
  },
  {
    path: "/offline_course_users",
    name: "OfflineCourse" as const,
    meta: {
      title: "オフライン_合説特別講演出席者一覧",
      privilege: true,
      predicate: PRIVILEGE_ENTRIES_READ
    },
    // Note: ブースチェックインと同じ権限を参照する
    // c.f. https://onecareer.slack.com/archives/C036XEM2XGX/p1715849537448589
    beforeEnter(_to: Route, _from: Route, next: NavigationGuardNext) {
      ensureCheckinApproved(next);
    },
    component: () => import("@/components/Pages/OfflineCourse/index.vue")
  },
  {
    path: "/video_bundle_unlocking_logs",
    name: "VideoBundleUnlockingLogs" as const,
    meta: {
      title: "動画エントリー一覧",
      privilege: true,
      predicate: PRIVILEGE_ENTRIES_READ
    },
    beforeEnter(_to: Route, _from: Route, next: NavigationGuardNext) {
      ensureVideoBundleUnlockingLogsApproved(next);
    },
    component: () =>
      import("@/components/Pages/VideoBundleUnlockingLogs/index.vue")
  },
  {
    path: "/selected_partner_entry_users/",
    name: "SelectedPartnerEntryUsers" as const,
    meta: {
      title: "選考ステータス一括変更 確認"
    },
    component: () =>
      import("@/components/Pages/SelectedPartnerEntryUsers/index.vue")
  },
  {
    path: "/job/applicants/:user_id",
    name: "Applicant" as const,
    meta: {
      title: "応募者詳細",
      privilege: true,
      predicate: PRIVILEGE_ENTRIES_READ
    },
    component: () => import("@/components/Pages/Applicant/index.vue")
  },
  {
    path: "/job/applicants/:user_id/resume/:event_id",
    name: "ApplicantResume" as const,
    meta: {
      title: "応募者レジュメ",
      privilege: true,
      predicate: PRIVILEGE_ENTRIES_READ
    },
    component: () => import("@/components/Pages/Applicant/Resume.vue")
  },
  {
    path: "/official/edit",
    name: "OfficialEdit" as const,
    meta: {
      title: "企業ページ"
    },
    component: () => import("@/components/Pages/Official/index.vue")
  },
  {
    path: "/announcements",
    name: "Announcements" as const,
    meta: {
      title: "運営事務局からのお知らせ"
    },
    component: () => import("@/components/Pages/Announcements/index.vue")
  },
  {
    path: "/dashboard",
    name: "Dashboard" as const,
    meta: {
      title: "ダッシュボード"
    },
    component: () => import("@/components/Pages/Dashboard/index.vue")
  },
  {
    path: "/accounts/company_logo",
    name: "AccountsCompanyLogo" as const,
    meta: {
      title: "アカウント設定 - 企業ロゴの設定",
      privilege: true,
      predicate: PRIVILEGE_COMPANY_PROPS_WRITE
    },
    component: () => import("@/components/Pages/Accounts/CompanyLogo/index.vue")
  },
  {
    path: "/performances/events",
    name: "PerformancesEvents" as const,
    meta: {
      title: "パフォーマンス - イベントページ"
    },
    component: () => import("@/components/Pages/PerformanceEvent/index.vue")
  },
  {
    path: "/performances/official",
    name: "PerformancesOfficial" as const,
    meta: {
      title: "パフォーマンス - 企業ページ"
    },
    component: () => import("@/components/Pages/PerformanceOfficial/index.vue")
  },
  // スカウトパフォーマンスレポートにバグがあるので暫定的にアクセスできないようにする。対応時期は未定
  // {
  //   path: "/performances/scout",
  //   name: "PerformancesScout" as const,
  //   meta: {
  //     title: "パフォーマンス - スカウト"
  //   },
  //   component: () => import("@/components/Pages/PerformanceScout/index.vue")
  // },
  {
    path: "/scout/candidates",
    name: "ScoutCandidates" as const,
    meta: {
      title: "スカウト - スカウト候補者一覧"
    },
    component: () => import("@/components/Pages/Scout/Candidates/Index.vue"),
    beforeEnter: (_to: any, _from: any, next: any) => {
      ensureScoutPrivilege(next);
    }
  },
  {
    path: "/scout/messages",
    name: "ScoutMessages" as const,
    meta: {
      title: "スカウト - メッセージ"
    },
    component: () => import("@/components/Pages/Scout/Messages/index.vue"),
    beforeEnter: (_to: any, _from: any, next: any) => {
      // 代理ログインの場合はメッセージ画面に遷移させない
      if (store.state.domains.user.partnerUser?.logged_in_by_proxy) {
        store.commit("common/updateApiError", {
          code: HttpStatusCode.Forbidden,
          message: "Forbidden"
        });
        return;
      }
      ensureScoutPrivilege(next);
    }
  },
  {
    path: "/scout/templates",
    // システム的にはsearch_templateと混同する可能性があるため、MessageTemplatesとする
    name: "ScoutMessageTemplates" as const,
    meta: {
      title: "スカウト - テンプレート"
    },
    component: () =>
      import("@/components/Pages/Scout/MessageTemplates/index.vue"),
    beforeEnter: (_to: any, _from: any, next: any) => {
      ensureScoutPrivilege(next);
    }
  },
  {
    path: "/agent_scout/acceptances",
    name: "AgentScoutAcceptances" as const,
    meta: {
      title: "スカウトエージェント - 受諾者一覧",
      privilege: true,
      predicates: [PRIVILEGE_ENTRIES_READ, PRIVILEGE_AGENT_SCOUT]
    },
    beforeEnter(_to: Route, _from: Route, next: NavigationGuardNext) {
      ensureScoutAgentContract(next);
    },
    component: () =>
      import("@/components/Pages/AgentScout/Acceptances/index.vue")
  },
  {
    path: "/agent_scout/acceptances/:user_id",
    name: "AgentScoutAcceptance" as const,
    meta: {
      title: "受諾者詳細",
      privilege: true,
      predicates: [PRIVILEGE_ENTRIES_READ, PRIVILEGE_AGENT_SCOUT]
    },
    beforeEnter(_to: Route, _from: Route, next: NavigationGuardNext) {
      ensureScoutAgentContract(next);
    },
    component: () =>
      import("@/components/Pages/AgentScout/Acceptance/index.vue")
  },
  /*
  NOTE: Rails上にrouteが存在するため、以下のpathは利用禁止
  関連情報
  https://onecareer.atlassian.net/browse/OCCA-1281
  https://github.com/OneCareerJP/cloud_backend/pull/56
  {
    path: "/partner_applications/new",
  },
  {
    path: "/partner_applications/wait",
  },
  */
  {
    path: "/error/unauthorized",
    name: "ErrorUnauthorized" as const,
    meta: {
      title: "本機能をご利用いただく権限がございません",
      ignoreAuth: true
    },
    component: () => import("@/components/Pages/Error/Unauthorized")
  },
  {
    path: "*",
    name: "ErrorNotFound" as const,
    meta: {
      title: "お探しのページは見つかりませんでした",
      ignoreAuth: true
    },
    component: ErrorNotFound
  }
];

export const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes
});

const adjustMenuType = async (to: Route) => {
  // MenuTypeがlocalStorageから復元されていない場合があるため、ここで復元する
  restoreMenuTypeFromLocalStorage();
  // 人事画面で閲覧できないページは ensureScoutAgentContract でエージェント画面に遷移させるようにしているため、人事画面滞在中はここでは何もしない
  if (!store.getters["domains/user/isScoutAgentMenuActive"]) {
    return;
  }

  if (!hasPrivilege(PRIVILEGE_AGENT_SCOUT)) {
    // エージェント向けスカウトの権限を持たないユーザーの場合は、人事画面に切り替える
    store.commit("domains/user/changeMenuType", MenuType.MENU_DEFAULT);
    saveMenuTypeToLocalStorage(MenuType.MENU_DEFAULT);
  }

  // 利用規約系のページはエージェント画面でも表示可能とする
  if (to.path.startsWith("/terms")) {
    return;
  }

  // エージェント画面で表示可能なページのホワイトリスト
  // agentMenuConfigsに定義されているページとそれに関連する子ページ、およびエラー系のページは許容する
  // c.f. https://github.com/OneCareerJP/cloud/blob/a4200c5fe06d6c71ce7e23c29f1f8cc02204b429/frontend/src/helpers/hooks/useOccNavigation.ts#L57
  // agentMenuConfigsをexportして利用したいが、useOccNavigationはsetup関数内でしか利用できないため、ここでホワイトリストを定義しています
  const agentMenuWhiteList = [
    "ScoutCandidates",
    "ScoutMessages",
    "ScoutMessageTemplates",
    "AgentScoutAcceptances",
    "AgentScoutAcceptance",
    "Files",
    "ErrorUnauthorized",
    "ErrorNotFound"
  ];

  if (
    to.name === null ||
    to.name === undefined ||
    !agentMenuWhiteList.includes(to.name)
  ) {
    // ホワイトリストに含まれないページの場合、メニューの状態を人事画面に切り替える
    store.commit("domains/user/changeMenuType", MenuType.MENU_DEFAULT);
    saveMenuTypeToLocalStorage(MenuType.MENU_DEFAULT);
  }
};

router.beforeEach(async (to, from, next) => {
  if (from.path !== to.path) {
    // ページ遷移時にエラーはリセットする
    store.commit("common/resetApiError");
    store.commit("common/resetGrpcError");
  }

  if (to.name === "AuthorizationSignInToken" && !hasToken()) {
    authorizationSignInToken(to.query.oc_token as string);
    return;
  } else if (to.name === "AuthorizationSignInToken" && hasToken()) {
    next({
      name: "Home"
    });
    return;
  }

  // NOTE: ログイン状態で、authPageへ訪れたら、Homeへリダイレクト
  if (hasToken() && to.meta?.authPage) {
    next({
      name: "Home"
    });
    return;
  }

  // NOTE: ログイン直後
  if (to.query.isLoggedIn) {
    setTokenToStore();
    await getPartnerUserAndContracts();
    setGAParams();
    setZendeskWidget();
    setSentryUser();

    const vm = new Vue();
    sendGaEvent(vm, { gaEventCategory: "ログイン" });

    if (redirectWhenIsNotOnboardingFinished()) return;
  }

  // NOTE: ログアウト直後
  if (to.query.isLoggedOut) {
    resetZendeskWidget();
    setGAUserParams(undefined);
  }

  if (hasToken()) {
    // ログイン済みならPartnerUserとContractをロードする
    // 権限判定やメニュー種別（エージェント・人事画面）の補正に必要
    await getPartnerUserAndContracts();

    if (to.meta?.privilege) {
      if (to.meta?.predicate && !hasPrivilege(to.meta?.predicate)) {
        // NOTE: 権限がない場合
        store.commit("common/updateApiError", {
          code: HttpStatusCode.Forbidden,
          message: "Forbidden"
        });
        return;
      }
      if (to.meta?.predicates) {
        const predicates = to.meta?.predicates as string[];
        // predicatesで指定された権限のいずれかを持っていない場合アクセス不可とする
        if (predicates.some(predicate => !hasPrivilege(predicate))) {
          store.commit("common/updateApiError", {
            code: HttpStatusCode.Forbidden,
            message: "Forbidden"
          });
          return;
        }
      }
    }

    // ログイン中の場合、遷移先のページによってメニュー種別（エージェント画面・人事画面）を補正する
    await adjustMenuType(to);
  }

  // NOTE: リダイレクト処理
  if (redirectToInitialPage(to)) return;

  next();
});

router.afterEach(to => {
  setTitle(to.meta?.title);
  if (hasToken()) {
    setNoindex();
  }
});

const gaConfig: { [key: string]: any } = {
  id: process.env.VUE_APP_GA_ID
};

Vue.use(VueGtm, {
  id: process.env.VUE_APP_GTM_ID,
  debug:
    process.env["NODE_ENV"] === "production" ||
    process.env["NODE_ENV"] === "staging"
      ? false
      : true,
  vueRouter: router
});

Vue.use(
  VueGtag,
  {
    config: gaConfig,
    pageTrackerEnabled: true,
    pageTrackerTemplate(to: any) {
      return {
        page_title: setGaPageTitle(to.meta.title, location.pathname),
        page_location: to.fullPath
      };
    }
  },
  router
);

/**
 * 4.x系で使えるuseRouterの代替メソッド
 * setup()関数内でのみ使用可能
 * @returns Routerオブジェクト
 */
export function useRouter(): VueRouter {
  // インスタンスにアクセス
  const instance = useComponentInstance();

  return instance.$router;
}

/**
 * 4.x系で使えるuseRouteの代替メソッド
 * setup()関数内のみで使用可能
 * @returns Routeオブジェクト
 */
export function useRoute(): Route {
  // インスタンスにアクセス
  const instance = useComponentInstance();

  return instance.$route;
}

export default router;
